import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Clock, User, Send } from "lucide-react";
import { useLang, ADDRESS } from "../i18n";
import { Reveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", arrival: "", departure: "", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", phone: "", arrival: "", departure: "", message: "", website: "" });
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  const infoRows = [
    { icon: MapPin, label: t.contact.addressLabel, value: ADDRESS, id: "address" },
    { icon: Clock, label: t.contact.checkinLabel, value: t.contact.checkin, id: "checkin" },
    { icon: Clock, label: t.contact.checkoutLabel, value: t.contact.checkout, id: "checkout" },
    { icon: User, label: t.contact.hostLabel, value: t.contact.host, id: "host" },
  ];

  return (
    <section id="contatti" data-testid="contact-section" className="py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>{t.contact.overline}</Overline>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl tracking-tight mt-4">{t.contact.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#14213D]/70 leading-relaxed max-w-md">{t.contact.desc}</p>
          </Reveal>
          <div className="mt-12 space-y-6">
            {infoRows.map((row, i) => (
              <Reveal key={row.id} delay={i * 0.06}>
                <div data-testid={`contact-info-${row.id}`} className="flex items-start gap-4">
                  <row.icon size={18} className="text-[#2852A6] mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">{row.label}</p>
                    <p className="font-serif text-xl mt-1">{row.value}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-10 lg:pt-24">
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] h-0 w-0 opacity-0 pointer-events-none"
            />
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">
                  {t.contact.form.name}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-form-name"
                  type="text"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-3 w-full bg-transparent border-b border-[#14213D]/25 focus:border-[#D39A2E] outline-none py-3 text-lg transition-colors duration-300"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">
                  {t.contact.form.email}
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-form-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-3 w-full bg-transparent border-b border-[#14213D]/25 focus:border-[#D39A2E] outline-none py-3 text-lg transition-colors duration-300"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-10">
              <div>
                <label htmlFor="contact-phone" className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">
                  {t.contact.form.phone}
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-form-phone"
                  type="tel"
                  required
                  minLength={5}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-3 w-full bg-transparent border-b border-[#14213D]/25 focus:border-[#D39A2E] outline-none py-3 text-lg transition-colors duration-300"
                />
              </div>
              <div>
                <label htmlFor="contact-arrival" className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">
                  {t.contact.form.arrival}
                </label>
                <input
                  id="contact-arrival"
                  data-testid="contact-form-arrival"
                  type="date"
                  required
                  value={form.arrival}
                  onChange={(e) => setForm({ ...form, arrival: e.target.value })}
                  className="mt-3 w-full bg-transparent border-b border-[#14213D]/25 focus:border-[#D39A2E] outline-none py-3 text-lg transition-colors duration-300"
                />
              </div>
              <div>
                <label htmlFor="contact-departure" className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">
                  {t.contact.form.departure}
                </label>
                <input
                  id="contact-departure"
                  data-testid="contact-form-departure"
                  type="date"
                  required
                  value={form.departure}
                  onChange={(e) => setForm({ ...form, departure: e.target.value })}
                  className="mt-3 w-full bg-transparent border-b border-[#14213D]/25 focus:border-[#D39A2E] outline-none py-3 text-lg transition-colors duration-300"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.2em] text-[#14213D]/50">
                {t.contact.form.message}
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-form-message"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-3 w-full bg-transparent border-b border-[#14213D]/25 focus:border-[#D39A2E] outline-none py-3 text-lg resize-none transition-colors duration-300"
              />
            </div>
            <div>
              <button
                data-testid="contact-form-submit"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-3 rounded-full bg-[#14213D] text-[#F9F8F6] px-8 py-4 text-sm tracking-wide hover:bg-[#2852A6] hover:scale-[1.03] disabled:opacity-50 disabled:hover:scale-100 transition-[background-color,transform,opacity] duration-300"
              >
                {sending ? t.contact.form.sending : t.contact.form.submit}
                <Send size={15} />
              </button>
              <p data-testid="contact-form-privacy" className="mt-6 text-xs text-[#14213D]/50 leading-relaxed">
                {t.contact.form.privacy}
                <br />
                {t.contact.form.forward}
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
